import { useEffect, useState } from 'react'
import { Plus, Loader2, Trash2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetFixedCosts,
  useUpsertFixedCost,
  useDeleteFixedCost,
} from '../../hooks/backend/settingsPage'
import { HelpText } from '../../components/HelpText'

interface FixedCost {
  id: string
  name: string
  cost: number
  sort_order: number
}

const money = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })

export default function FixedCostsSettings() {
  const getFixedCosts = useGetFixedCosts()
  const upsertFixedCost = useUpsertFixedCost()
  const deleteFixedCost = useDeleteFixedCost()

  const items = (getFixedCosts.data as FixedCost[] | undefined) ?? []

  const [name, setName] = useState('')
  const [cost, setCost] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    void getFixedCosts.trigger({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refresh = () => getFixedCosts.trigger({}, { skipCache: true })

  const add = async () => {
    const n = name.trim()
    const c = Number(cost)
    if (!n) {
      toast.error('Name is required')
      return
    }
    if (Number.isNaN(c) || c < 0) {
      toast.error('Cost must be a non-negative number')
      return
    }
    try {
      setBusy('add')
      await upsertFixedCost.trigger({ name: n, cost: c })
      setName('')
      setCost('')
      await refresh()
    } catch {
      toast.error('Failed to add fixed cost')
    } finally {
      setBusy(null)
    }
  }

  const remove = async (id: string) => {
    try {
      setBusy(id)
      await deleteFixedCost.trigger({ id })
      await refresh()
    } catch {
      toast.error('Failed to remove fixed cost')
    } finally {
      setBusy(null)
    }
  }

  const total = items.reduce((sum, i) => sum + (i.cost ?? 0), 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fixed Costs</CardTitle>
        <CardDescription><HelpText>
          Per-unit costs added to every product's cost on top of its recipe (e.g. bags,
          labels, packaging). The total below is added to each product.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add form */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr,10rem,auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="fc-name">Name</Label>
            <Input
              id="fc-name"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void add() } }}
              placeholder="e.g. Bag"
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fc-cost">Cost per unit</Label>
            <Input
              id="fc-cost"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={cost}
              onChange={e => setCost(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void add() } }}
              placeholder="e.g. 0.15"
              className="h-9"
            />
          </div>
          <Button
            type="button"
            onClick={() => void add()}
            disabled={busy === 'add' || !name.trim() || !cost.trim()}
            className="gap-1.5 shrink-0"
          >
            {busy === 'add' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add
          </Button>
        </div>

        {/* Existing fixed costs */}
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No fixed costs yet.</p>
        ) : (
          <div className="divide-y rounded-md border">
            {items.map(item => (
              <div key={item.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="font-medium">{item.name}</span>
                <div className="flex items-center gap-3">
                  <span className="tabular-nums">{money(item.cost)}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => void remove(item.id)}
                    disabled={busy === item.id}
                    aria-label={`Remove ${item.name}`}
                  >
                    {busy === item.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between px-3 py-2 text-sm font-semibold">
              <span>Total per product</span>
              <span className="tabular-nums">{money(total)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
